<template>
  <v-dialog
    :model-value="modelValue"
    width="560"
    max-width="calc(100vw - 32px)"
    persistent
    @update:model-value="emit('update:modelValue', $event)"
  >
    <v-card v-if="transaction">
      <v-form
        ref="form"
        :disabled="loading"
        validate-on="invalid-input lazy"
        @submit.prevent="save"
      >
        <v-card-title>{{ t("transactions.split.title") }}</v-card-title>
        <v-card-text class="pt-2" style="max-height: 60vh; overflow-y: auto">
          <div class="split-table">
            <div class="split-row split-row--header text-sm font-medium text-medium-emphasis">
              <div class="split-label" />
              <div class="split-category">{{ t("common.category") }}</div>
              <div class="split-amount">{{ t("common.amount") }}</div>
              <div class="split-actions" />
            </div>

            <div v-for="(line, index) in lines" :key="line.key" class="split-row">
              <div class="split-label text-sm text-medium-emphasis">
                {{ t("transactions.split.line", { index: index + 1 }) }}
              </div>

              <v-autocomplete
                v-model="line.trackedCategoryId"
                :items="selectorItems"
                item-title="name"
                item-value="id"
                :item-props="getCategoryItemProps(index)"
                :placeholder="t('transfers.selectCategory')"
                :aria-label="
                  t('transactions.split.line', { index: index + 1 }) + ' — ' + t('common.category')
                "
                :rules="[required, activeCategory, uniqueCategory]"
                variant="outlined"
                density="compact"
                hide-details="auto"
                class="split-category"
              />

              <v-text-field
                v-model.number="line.amount"
                type="number"
                min="0"
                step="0.01"
                :aria-label="
                  t('transactions.split.line', { index: index + 1 }) + ' — ' + t('common.amount')
                "
                :rules="[required, positiveNumber]"
                variant="outlined"
                density="compact"
                hide-details="auto"
                :suffix="transaction.currencyCode"
                class="split-amount"
                @update:model-value="onAmountInput(index, $event)"
              />

              <div class="split-actions">
                <v-btn
                  icon="mdi-delete"
                  variant="text"
                  color="error"
                  size="small"
                  :disabled="loading || lines.length <= 1"
                  @click="removeLine(line.key)"
                />
              </div>
            </div>

            <v-btn
              class="mt-1"
              variant="text"
              prepend-icon="mdi-plus"
              :disabled="loading || !canAddLine"
              @click="addLine"
            >
              {{ t("transactions.split.addLine") }}
            </v-btn>

            <div class="split-totals mt-4">
              <div class="split-row split-row--total">
                <div class="split-label" />
                <div class="split-category text-sm">
                  {{ t("transactions.split.allocatedLabel") }}
                </div>
                <div
                  class="split-amount text-sm font-medium text-end"
                  :class="isBalanced ? 'text-primary' : 'text-error'"
                >
                  <v-icon v-if="!isBalanced" icon="mdi-alert" size="small" class="me-1" />
                  {{ allocatedFormatted }}
                </div>
                <div class="split-actions" />
              </div>
              <div class="split-row split-row--total">
                <div class="split-label" />
                <div class="split-category text-sm text-medium-emphasis">
                  {{ t("transactions.split.transactionTotal") }}
                </div>
                <div class="split-amount text-sm text-medium-emphasis text-end">
                  {{ absoluteAmountFormatted }}
                </div>
                <div class="split-actions" />
              </div>
              <div v-if="!isBalanced" class="split-row split-row--total">
                <div class="split-label" />
                <div class="split-category text-sm text-error">
                  {{ t("transactions.split.remainingLabel") }}
                </div>
                <div class="split-amount text-sm text-error text-end">
                  {{ remainingFormatted }}
                </div>
                <div class="split-actions" />
              </div>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn :disabled="loading" @click="emit('update:modelValue', false)">{{
            t("common.cancel")
          }}</v-btn>
          <v-btn
            color="primary"
            variant="flat"
            type="submit"
            :loading="loading"
            :disabled="loading || !(canSaveSplit || canSaveSingle)"
          >
            {{ t("common.save") }}
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script lang="ts" setup>
import { useI18n } from "@/composables/useI18n";
import { useRules } from "@/composables/useRules";
import type { CategorySelectItem } from "@/composables/useTransactionCategories";
import { formatCurrency } from "@/utils/tools";
import type { Transaction } from "@/utils/types";
import {
  areTrackedCategorySplitsBalanced,
  getTrackedCategoryAssignment,
  validateTrackedCategorySplits,
  type TrackedCategorySplit,
} from "@aircap/common/trackedCategory";
import { computed, ref, watch } from "vue";
import type { VForm } from "vuetify/components";

type SplitLine = {
  key: string;
  trackedCategoryId: string | null;
  amount: number | null;
};

const newLineKey = () => crypto.randomUUID();

const props = defineProps<{
  modelValue: boolean;
  transaction: Transaction | null;
  selectorItems: CategorySelectItem[];
  categoryItemProps: (item: CategorySelectItem) => Record<string, unknown>;
  resolveCategoryName: (categoryId: string) => string | undefined;
  activeCategoryIds: string[];
  loading: boolean;
}>();

const emit = defineEmits<{
  "update:modelValue": [value: boolean];
  save: [splits: TrackedCategorySplit[]];
  saveSingle: [categoryId: string];
}>();

const { t, locale } = useI18n();
const { required, positiveNumber } = useRules();
const form = ref<VForm>();
const lines = ref<SplitLine[]>([]);
const firstDrivesOthers = ref(false);

const absoluteAmount = computed(() => Math.abs(props.transaction?.amount ?? 0));

const absoluteAmountFormatted = computed(() =>
  formatCurrency({
    value: absoluteAmount.value,
    currencyCode: props.transaction?.currencyCode ?? "EUR",
    locale: locale.value,
  }),
);

const round2 = (value: number) => Math.round(value * 100) / 100;

const isCompleteLine = (
  line: SplitLine,
): line is SplitLine & { trackedCategoryId: string; amount: number } =>
  typeof line.trackedCategoryId === "string" &&
  line.trackedCategoryId.length > 0 &&
  typeof line.amount === "number" &&
  line.amount > 0 &&
  props.activeCategoryIds.includes(line.trackedCategoryId);

const allLinesComplete = computed(
  () => lines.value.length > 0 && lines.value.every(isCompleteLine),
);

const toSplits = (): TrackedCategorySplit[] =>
  lines.value.filter(isCompleteLine).map((line) => ({
    trackedCategoryId: line.trackedCategoryId,
    amount: round2(line.amount),
  }));

const allocatedTotal = computed(() =>
  round2(
    lines.value.reduce((sum, line) => {
      if (typeof line.amount !== "number" || Number.isNaN(line.amount)) return sum;
      return sum + line.amount;
    }, 0),
  ),
);

const allocatedFormatted = computed(() =>
  formatCurrency({
    value: allocatedTotal.value,
    currencyCode: props.transaction?.currencyCode ?? "EUR",
    locale: locale.value,
  }),
);

const remainingAmount = computed(() => round2(absoluteAmount.value - allocatedTotal.value));

const remainingFormatted = computed(() =>
  formatCurrency({
    value: remainingAmount.value,
    currencyCode: props.transaction?.currencyCode ?? "EUR",
    locale: locale.value,
  }),
);

const isBalanced = computed(() => {
  if (!props.transaction) return true;
  return Math.abs(remainingAmount.value) <= 0.01;
});

const canSaveSplit = computed(() => {
  if (!props.transaction || !allLinesComplete.value) return false;
  return validateTrackedCategorySplits(toSplits(), {
    transactionAmount: props.transaction.amount,
    activeCategoryIds: props.activeCategoryIds,
  }).ok;
});

const canSaveSingle = computed(() => {
  if (!props.transaction || !allLinesComplete.value || lines.value.length !== 1) return false;
  const [line] = toSplits();
  if (!line) return false;
  return areTrackedCategorySplitsBalanced([line], props.transaction.amount);
});

const activeCategory = (value: unknown) => {
  if (!value || typeof value !== "string") return true;
  if (props.activeCategoryIds.includes(value)) return true;
  const name = props.resolveCategoryName(value);
  return t("transactions.split.deletedCategory", { name: name ?? value });
};

const uniqueCategory = (value: unknown) => {
  if (!value || typeof value !== "string") return true;
  const count = lines.value.filter((line) => line.trackedCategoryId === value).length;
  return count <= 1 || t("transactions.split.duplicateCategory");
};

const getCategoryItemProps = (lineIndex: number) => (item: CategorySelectItem) => {
  const base = props.categoryItemProps(item);
  if (item.header) return base;

  const usedOnOtherLine = lines.value.some(
    (line, index) => index !== lineIndex && line.trackedCategoryId === item.id,
  );
  return {
    ...base,
    disabled: Boolean(base.disabled) || usedOnOtherLine,
  };
};

const selectableCategoryCount = computed(
  () => props.selectorItems.filter((item) => !item.header).length,
);

const canAddLine = computed(() => lines.value.length < selectableCategoryCount.value);

const emptyLine = (): SplitLine => ({
  key: newLineKey(),
  trackedCategoryId: null,
  amount: null,
});

const parseAmountInput = (value: unknown) => {
  if (typeof value === "number") return Number.isNaN(value) ? null : value;
  if (typeof value !== "string" || value === "") return null;
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const clearOtherAmounts = () => {
  for (let i = 1; i < lines.value.length; i++) {
    const line = lines.value[i];
    if (line) line.amount = null;
  }
};

const distributeRemainderFromFirst = () => {
  if (lines.value.length < 2) return;

  const firstAmount = lines.value[0]?.amount;
  if (typeof firstAmount !== "number" || Number.isNaN(firstAmount) || firstAmount <= 0) {
    clearOtherAmounts();
    return;
  }

  const remainingCents = Math.round((absoluteAmount.value - firstAmount) * 100);
  const otherCount = lines.value.length - 1;

  // Need at least 1 cent per other row so amounts stay positive and savable.
  if (remainingCents < otherCount) {
    clearOtherAmounts();
    return;
  }

  const baseCents = Math.floor(remainingCents / otherCount);
  let leftoverCents = remainingCents % otherCount;
  for (let i = 1; i < lines.value.length; i++) {
    const line = lines.value[i];
    if (!line) continue;
    const cents = baseCents + (leftoverCents > 0 ? 1 : 0);
    if (leftoverCents > 0) leftoverCents -= 1;
    line.amount = cents / 100;
  }
};

const onAmountInput = (index: number, value: unknown) => {
  if (index !== 0) {
    firstDrivesOthers.value = false;
    return;
  }
  const firstLine = lines.value[0];
  if (!firstLine) return;
  // Keep in sync before distributing in case this listener runs before v-model.
  firstLine.amount = parseAmountInput(value);
  firstDrivesOthers.value = true;
  distributeRemainderFromFirst();
};

const addLine = () => {
  lines.value.push(emptyLine());
  if (firstDrivesOthers.value) distributeRemainderFromFirst();
};

const removeLine = (key: string) => {
  if (lines.value.length <= 1) return;
  const index = lines.value.findIndex((line) => line.key === key);
  if (index === -1) return;
  lines.value.splice(index, 1);
  if (firstDrivesOthers.value) distributeRemainderFromFirst();
};

const lineFromSplit = (split: TrackedCategorySplit): SplitLine => ({
  key: newLineKey(),
  trackedCategoryId: split.trackedCategoryId,
  amount: split.amount,
});

watch(
  () => [props.modelValue, props.transaction] as const,
  ([open, transaction]) => {
    if (!open || !transaction) return;

    const currentAssignment = getTrackedCategoryAssignment(transaction);
    switch (currentAssignment.mode) {
      case "split":
        lines.value = currentAssignment.splits.map(lineFromSplit);
        // Keep saved proportions until the user edits the first amount.
        firstDrivesOthers.value = false;
        break;
      case "single":
        lines.value = [
          lineFromSplit({
            trackedCategoryId: currentAssignment.trackedCategoryId,
            amount: currentAssignment.amount,
          }),
          emptyLine(),
        ];
        firstDrivesOthers.value = false;
        break;
      case "none":
        lines.value = [emptyLine(), emptyLine()];
        firstDrivesOthers.value = false;
        break;
    }
    form.value?.resetValidation();
  },
);

const save = async () => {
  if (props.loading) return;
  const { valid } = (await form.value?.validate()) ?? { valid: false };
  if (!valid) return;

  if (canSaveSplit.value) {
    emit("save", toSplits());
    return;
  }
  if (canSaveSingle.value) {
    const [line] = toSplits();
    if (line) emit("saveSingle", line.trackedCategoryId);
  }
};
</script>

<style scoped>
.split-table {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.split-row {
  display: grid;
  grid-template-columns: minmax(96px, auto) minmax(0, 1fr) minmax(96px, 140px) 40px;
  gap: 8px;
  align-items: start;
}

.split-row--header {
  padding-inline: 4px 0;
}

.split-row--total {
  align-items: center;
  min-height: 28px;
}

.split-label {
  display: flex;
  align-items: center;
  min-height: 40px;
  white-space: nowrap;
}

.split-amount :deep(input) {
  text-align: end;
}

.split-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
}

.split-totals {
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  padding-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

@media (max-width: 520px) {
  .split-row--header {
    display: none;
  }

  .split-row:not(.split-row--total) {
    grid-template-columns: minmax(0, 1fr) 40px;
    grid-template-areas:
      "label actions"
      "category category"
      "amount amount";
  }

  .split-row:not(.split-row--total) .split-label {
    grid-area: label;
    min-height: auto;
  }

  .split-row:not(.split-row--total) .split-actions {
    grid-area: actions;
    min-height: auto;
    justify-content: flex-end;
  }

  .split-row:not(.split-row--total) .split-category {
    grid-area: category;
  }

  .split-row:not(.split-row--total) .split-amount {
    grid-area: amount;
  }

  .split-row--total {
    grid-template-columns: minmax(0, 1fr) auto;
  }

  .split-row--total .split-label,
  .split-row--total .split-actions {
    display: none;
  }
}
</style>
